import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiErrorMessage, setToken, getToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        if (!getToken()) {
            setUser(false);
            return null;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            setToken(null);
            setUser(false);
            return null;
        }
    }, []);

    useEffect(() => {
        (async () => {
            await refresh();
            setLoading(false);
        })();
    }, [refresh]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.access_token) setToken(data.access_token);
            setUser(data);
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: apiErrorMessage(e) };
        }
    };

    const register = async (name, email, password) => {
        try {
            const { data } = await api.post("/auth/register", { name, email, password });
            if (data.access_token) setToken(data.access_token);
            setUser(data);
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: apiErrorMessage(e) };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            /* ignore */
        }
        setToken(null);
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
